import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router";
import { Avatar, Badge, Button, Select } from "../design-system/components";
import { endpoints } from "../lib/api";
import { signOut } from "../lib/auth";
import { useSession } from "../lib/session";

const NAV = [
  { to: "/", label: "Overview" },
  { to: "/games", label: "Games" },
  { to: "/decks", label: "Decks" },
  { to: "/upload", label: "Upload" },
  { to: "/leaderboard", label: "Leaderboard" },
  { to: "/seasons", label: "Seasons" },
];

function useTheme(): [boolean, () => void] {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    try {
      localStorage.setItem("pra.theme", dark ? "dark" : "light");
    } catch {
      /* ignore */
    }
  }, [dark]);
  return [dark, () => setDark((d) => !d)];
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function AppShell() {
  const { user, profile, isAdmin, scopeUserId, setScope } = useSession();
  const shownName = profile?.tcglHandles?.[0] ?? user?.displayName ?? "?";
  const [dark, toggleTheme] = useTheme();
  const navigate = useNavigate();
  const users = useQuery({
    queryKey: ["admin", "users"],
    queryFn: endpoints.adminUsers,
    enabled: isAdmin,
  });
  const scoped = users.data?.users.find((u) => u.userId === scopeUserId);

  return (
    <div className="rk-app">
      <header className="rk-header">
        <div className="rk-header__inner">
          <Link className="rk-logo" to="/">
            <img src="/play-rough-mark-pink.svg" alt="" />
            <span className="rk-logo__word">
              <strong>Play Rough</strong>
              <em>Analytics</em>
            </span>
          </Link>
          <nav className="rk-nav">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className="rk-nav__item"
                data-active={undefined}
              >
                {({ isActive }) => (
                  <span data-active={isActive} className="rk-nav__item" style={{ padding: 0 }}>
                    {item.label}
                  </span>
                )}
              </NavLink>
            ))}
            {isAdmin && (
              <NavLink to="/admin" className="rk-nav__item">
                {({ isActive }) => (
                  <span data-active={isActive} className="rk-nav__item" style={{ padding: 0 }}>
                    Admin
                  </span>
                )}
              </NavLink>
            )}
          </nav>
          <div className="rk-header__actions">
            {isAdmin && users.data && (
              <div className="rk-admin-scope">
                <Select
                  size="sm"
                  value={scopeUserId ?? ""}
                  onChange={(v) => setScope(v === "" ? undefined : v)}
                  options={[
                    { value: "", label: "Viewing: me" },
                    ...users.data.users
                      .filter((u) => u.userId !== user?.userId)
                      .map((u) => ({ value: u.userId, label: `Viewing: ${u.displayName}` })),
                  ]}
                />
              </div>
            )}
            <button
              type="button"
              className="rk-iconbtn"
              aria-label="Toggle theme"
              onClick={toggleTheme}
            >
              {dark ? "☀" : "☾"}
            </button>
            <Link to="/settings" className="rk-user" style={{ textDecoration: "none" }}>
              <Avatar fallback={initials(shownName)} size="sm" />
              <div className="rk-user__meta">
                <span className="rk-user__name">{shownName}</span>
                <Badge variant={isAdmin ? "accent" : "outline"}>{user?.role}</Badge>
              </div>
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => {
                await signOut();
                navigate("/login");
                window.location.reload();
              }}
            >
              Sign out
            </Button>
          </div>
        </div>
      </header>
      {scoped && (
        <div className="rk-banner">
          <Badge variant="accent">Admin</Badge>
          <span>
            Viewing <strong>{scoped.displayName}</strong>&apos;s games and decks.
          </span>
          <Button variant="link" size="sm" onClick={() => setScope(undefined)}>
            Back to mine
          </Button>
        </div>
      )}
      <main className="rk-main">
        <Outlet />
      </main>
      <footer className="rk-footer">
        <img src="/play-rough-mark-charcoal.svg" alt="" />
        <span>Play Rough Analytics — deck stats from the games you export from TCG Live.</span>
      </footer>
    </div>
  );
}
